import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  OutlinedInput,
  OutlinedInputProps,
  SliderProps,
} from "@mui/material";
import { useState } from "react";
import {
  ControllerRenderProps,
  FieldError,
  FieldValues,
} from "react-hook-form";

import { useOnValueChanged } from "@/hooks";

import { LabelRow } from "../LabelRow/LabelRow";
import { Slider } from "../Slider/Slider";

interface SliderWithTextFieldProps<T extends FieldValues> {
  field: Omit<ControllerRenderProps<T>, "ref"> & { ref?: React.Ref<any> };
  error?: FieldError;
  label: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  step?: number;
  min?: number;
  max?: number;
  adornment?: string;
  outlinedInputProps?: OutlinedInputProps;
  sliderProps?: SliderProps;
  helperText?: string;
  committedOnly?: boolean;
  testId?: string;
  labelEnd?: React.ReactNode;
  textField?: React.ReactNode;
  withLabel?: boolean;
  hideInputWhenDisabled?: boolean;
}

export const SliderWithTextField = <T extends FieldValues>({
  field,
  error,
  label,
  tooltip,
  disabled,
  step = 1,
  min = 0,
  max = 100,
  adornment,
  outlinedInputProps,
  sliderProps,
  helperText,
  committedOnly = false,
  testId,
  labelEnd,
  textField,
  withLabel = true,
  hideInputWhenDisabled = false,
}: SliderWithTextFieldProps<T>) => {
  const [localValue, setLocalValue] = useState<number | string>(field.value);

  const handleChangeTextField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocalValue = e.target.value === "" ? "" : Number(e.target.value);

    setLocalValue(newLocalValue);
    field.onChange(newLocalValue);
  };

  const handleChangeSlider: SliderProps["onChange"] = (e, value) => {
    const newLocalValue = Array.isArray(value) ? value[0] : value;
    setLocalValue(newLocalValue);

    if (!committedOnly) {
      field.onChange(newLocalValue);
    }
  };

  const handleChangeCommittedSlider: SliderProps["onChangeCommitted"] = (
    e,
    value,
  ) => {
    if (committedOnly) {
      field.onChange(value);
    }
    field.onBlur();

    sliderProps?.onChangeCommitted?.(e, value);
  };

  const handleFieldValueChanged = () => {
    if (localValue !== field.value) {
      setLocalValue(field.value);
    }
  };

  // To sync local value if field.value is changed programmatically outside the component
  useOnValueChanged(field.value, handleFieldValueChanged);

  const isHidden = hideInputWhenDisabled && disabled;

  return (
    <div>
      <FormControl sx={{ width: "100%" }}>
        {withLabel && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <LabelRow
              useMuiLabel
              useHtmlLabel
              disabled={disabled}
              label={label}
              tooltip={tooltip}
              htmlFor={field.name}
            />
            {labelEnd}
          </Box>
        )}
        <Box
          sx={{
            display: isHidden ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            paddingTop: "4px",
          }}
        >
          <Slider
            aria-label={label}
            {...field}
            disabled={disabled}
            step={step}
            min={min}
            max={max}
            value={Number(localValue)}
            onChange={handleChangeSlider}
            {...sliderProps}
            onChangeCommitted={handleChangeCommittedSlider}
            data-testid={testId}
          />
          {textField ? (
            textField
          ) : (
            <OutlinedInput
              {...field}
              id={field.name}
              value={localValue}
              onChange={handleChangeTextField}
              disabled={disabled}
              size="small"
              endAdornment={
                adornment && (
                  <InputAdornment
                    position="end"
                    sx={{ margin: 0, "&& > *": { fontSize: 12 } }}
                  >
                    {adornment}
                  </InputAdornment>
                )
              }
              {...outlinedInputProps}
              inputProps={{
                step,
                min,
                max,
                type: "number",
                "aria-labelledby": "input-slider",
                ...outlinedInputProps?.inputProps,
              }}
              sx={{
                minWidth: 48,
                paddingRight: 1,
                ...outlinedInputProps?.sx,

                "& .MuiInputBase-input": {
                  textAlign: "right",
                  paddingLeft: 1,
                  paddingRight: 1,
                },
              }}
            />
          )}
        </Box>
        {!isHidden && error && (
          <FormHelperText error sx={{ mx: 0 }}>
            {error.message}
          </FormHelperText>
        )}
        {!isHidden && !error && helperText && (
          <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </div>
  );
};
